/**
 * La structure d'un énoncé, posée à l'AFFICHAGE — sur tous les énoncés texte, anciens et tapés
 * par le tuteur compris : ce n'est pas une marque du générateur, c'est la forme que le prompt
 * impose déjà (« Sous-questions : utiliser le format a), b), c) »).
 *
 * Les numéros de question n'ont besoin de rien : « 1. » et « 1) » sont des listes Markdown, et la
 * feuille (`richTextStyles`) en fait des pastilles. Les sous-questions, elles, ne sont pas des
 * listes : une ligne qui COMMENCE par « a) » — seule, ou juste après le numéro de sa question
 * (« 3. a) … ») — reçoit son étiquette `enonce-sous`.
 *
 * Les énoncés générés avant octobre 2026 collent parfois le numéro à la première sous-question
 * (« 3.a) … », la réparation côté IA écrit désormais « 3. a) ») : sans espace, Markdown n'y voit
 * pas un élément de liste, et la question perdait sa pastille. On rend l'espace.
 *
 * Une citation (`> …`) n'est jamais touchée — elle commence par `>`, pas par une lettre : c'est
 * le texte que l'élève travaille, rien n'y est marqué.
 */
const GLUED_FIRST_SUB_QUESTION = /^([ \t]*\d+[.)])(?=[a-h]\)[ \t])/gm
/**
 * « 1. Écris en chiffres : a) douze… » puis « b) … » à la ligne : la première sous-question
 * partage la ligne de sa consigne (énoncés anciens). Elle passe à la ligne, en saut dur, quand
 * la ligne suivante commence par « b) » — sans ce « b) », un « a) » en fin de phrase n'est pas
 * une sous-question (« entre a) et b) »). La ponctuation suit un mot, jamais un chiffre : le
 * point de « 3. a) » est celui du numéro de question, que la règle suivante garde en tête.
 */
const INLINE_FIRST_SUB_QUESTION = /([^\d\n])([:.?!])[ \t]+a\)[ \t]+(?=[^\n]*\n[ \t]*b\)[ \t])/g
const SUB_QUESTION_LINE = /^([ \t]*(?:\d+[.)][ \t]+)?)([a-h])\)[ \t]+/gm
/** Une question commence à une ligne « 1. » ou « 1) » posée en marge — jamais « 3.5 ». */
const QUESTION_LEAD = /^\d+[.)][ \t]+/
const SUB_QUESTION_START = /^[a-h]\)[ \t]/
const OPTION_LEAD = /^([ \t]*)(\d+)\)[ \t]+/

const occurrences = (text: string, char: string) => text.split(char).length - 1

/**
 * Une ligne d'options numérotées — « 1) 10⁻² ; 2) 10¹ ; 3) 10³ », la colonne d'un exercice
 * d'association — n'est pas une question : après son « n) », la même ligne porte « ; n+1) » ou
 * « , n+1) ». Markdown en faisait une liste, donc une pastille de question de plus, et son « 1 »
 * cassait la suite des numéros.
 *
 * Le numéro suivant, et lui seul : « 1. Voici ses notes : 12, 15, 9. » est une question. Pas un
 * « n+1) » qui ferme une parenthèse ouverte avant lui : « 1) Place A(1 ; 2). » en est une aussi.
 * Et pas le format « n. » : « 1. Calcule a ; 2. Calcule b » reste une question, comme avant.
 * Au format « n) », deux questions écrites sur une seule ligne ne se distinguent pas d'options :
 * elles se lisent comme des options, du texte rangé dans sa question. Aucun des 3 169 énoncés
 * rejoués le 21/10 ne l'écrit ainsi.
 */
function isOptionsLine(line: string): boolean {
    const lead = OPTION_LEAD.exec(line)
    if (lead == null) return false
    const rest = line.slice(lead[0].length)
    const next = new RegExp(String.raw`[;,][ \t]+${Number(lead[2]) + 1}\)[ \t]`, "g")
    return Array.from(rest.matchAll(next)).some((match) => {
        const before = rest.slice(0, match.index)
        return occurrences(before, "(") <= occurrences(before, ")")
    })
}

/** Le numéro de question qui ouvre la ligne, ou rien : ni une ligne d'options, ni « 3.5 ». */
function questionLead(line: string): string | null {
    const lead = QUESTION_LEAD.exec(line)?.[0]
    return lead != null && !isOptionsLine(line) ? lead : null
}

/**
 * « 1. Souligne… », une ligne vide, puis « a) Le ciel… » — la forme que le générateur écrit le
 * plus souvent : la ligne vide ferme l'item de liste, et les sous-questions retombaient dans la
 * marge de l'énoncé au lieu de se ranger sous leur question. Un paragraphe qui commence par une
 * sous-question, entre une question et le premier paragraphe qui n'en est pas une, est rentré de
 * la largeur du numéro : Markdown le range dans l'item. Les lignes qui le suivent sans ligne
 * vide (« Nom qualifié : ___ ») y restent d'elles-mêmes.
 */
function nestSubQuestions(value: string): string {
    let questionIndent: string | null = null
    return value
        .split("\n")
        .map((line, i, lines) => {
            // Seul le début d'un paragraphe compte : une ligne vide, ou rien, le précède.
            if (line.trim() === "" || lines[i - 1]?.trim()) return line
            const lead = questionLead(line)
            if (lead) questionIndent = " ".repeat(lead.length)
            // Une ligne d'options en marge se range sous sa question, comme une sous-question.
            else if (questionIndent !== null && (SUB_QUESTION_START.test(line) || isMarginOptions(line)))
                return questionIndent + line
            else if (!/^[ \t]/.test(line)) questionIndent = null
            return line
        })
        .join("\n")
}

/** « 3.a) » redevient « 3. a) » : la question retrouve son numéro, donc sa pastille. */
function separateGluedQuestions(value: string): string {
    return value.replace(GLUED_FIRST_SUB_QUESTION, "$1 ")
}

const isMarginOptions = (line: string) => !/^[ \t]/.test(line) && isOptionsLine(line)

/** Une ligne d'options reste du texte : son premier « ) », écrit en entité, n'ouvre pas de liste. */
function literalOptionLines(value: string): string {
    return value
        .split("\n")
        .map((line) => (isOptionsLine(line) ? line.replace(/^([ \t]*\d+)\)/, "$1&#41;") : line))
        .join("\n")
}

export function markEnonceStructure(value: string): string {
    return literalOptionLines(
        nestSubQuestions(separateGluedQuestions(value).replace(INLINE_FIRST_SUB_QUESTION, "$1$2  \na) ")).replace(
            SUB_QUESTION_LINE,
            (_line, lead: string, letter: string) => {
                return `${lead}<span class="enonce-sous">${letter})</span> `
            }
        )
    )
}
